import { huMobilNormalizalas } from '@ertekbecslo/kozos/telefon';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { supabase } from '../lib/supabase.ts';
import { SZEREP_CIMKE } from '../lib/tipusok.ts';
import { Betolto, Fokonyv, Gomb, HibaUzenet, Mezo, OldalFej } from '../ui/elemek.tsx';

export default function Profil() {
  const { profil, kijelentkezes, frissites } = useAuth();
  const { irodak, jogok } = useAktivIroda();
  const [nev, setNev] = useState(profil?.nev ?? '');
  const [telefon, setTelefon] = useState(profil?.telefon ?? '');
  const [telHiba, setTelHiba] = useState<string | null>(null);
  const [hiba, setHiba] = useState<unknown>(null);
  const [siker, setSiker] = useState(false);
  const [mentes, setMentes] = useState(false);
  const [kilepesHiba, setKilepesHiba] = useState<unknown>(null);

  // Csak az első betöltéskor töltjük fel az űrlapot, hogy a fülváltás ne írja felül a beírt értékeket.
  const profilId = profil?.user_id;
  useEffect(() => { setNev(profil?.nev ?? ''); setTelefon(profil?.telefon ?? ''); }, [profilId]);

  const tenyezok = useQuery({
    queryKey: ['mfa-tenyezok'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data.totp.filter((t) => t.status === 'verified');
    },
  });
  const tagsagok = useQuery({
    queryKey: ['sajat-tagsagok', profil?.user_id],
    enabled: !!profil,
    queryFn: async () => {
      const { data, error } = await supabase.from('office_members').select('office_id, szerep').eq('user_id', profil!.user_id).eq('aktiv', true);
      if (error) throw error;
      return data;
    },
  });

  if (!profil) return <Betolto />;

  async function mentes_(e: FormEvent) {
    e.preventDefault();
    setSiker(false);
    setHiba(null);
    let tel: string | null = null;
    if (telefon.trim() !== '') {
      const t = huMobilNormalizalas(telefon);
      if (!t.ok) { setTelHiba('Csak magyar mobilszám adható meg (+36 20/30/31/50/70).'); return; }
      tel = t.e164;
    }
    setTelHiba(null);
    if (nev.trim() === '') { setHiba(new Error('A név kötelező.')); return; }
    setMentes(true);
    try {
      const { error } = await supabase.from('profiles').update({ nev: nev.trim(), telefon: tel }).eq('user_id', profil!.user_id);
      if (error) throw error;
      await frissites();
      setSiker(true);
    } catch (err) {
      setHiba(err);
    } finally {
      setMentes(false);
    }
  }

  const irodaNev = (id: string) => irodak.find((i) => i.id === id)?.nev ?? id;
  return (
    <div className="grid max-w-xl gap-5">
      <OldalFej cim="Profil" leiras="A saját adataid, a belépési védelem és a jogaid." />
      <Fokonyv>
      <section className="px-5 py-5">
        <form onSubmit={(e) => void mentes_(e)} className="grid gap-4">
          <Mezo label="Név" value={nev} onChange={(e) => { setNev(e.target.value); setSiker(false); }} required />
          <Mezo label="E-mail" value={profil.email} readOnly suly="Az e-mail-címedet nem módosíthatod itt." />
          <Mezo label="Telefon" type="tel" value={telefon} hiba={telHiba} onChange={(e) => { setTelefon(e.target.value); setSiker(false); }}
            suly="Belső elérhetőség az irodának; a nyilvános oldalon nem jelenik meg." />
          {hiba instanceof Error && !('code' in hiba) ? <p role="alert" className="text-sm text-hiba">{hiba.message}</p> : <HibaUzenet hiba={hiba} />}
          {siker && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Mentve.</p>}
          <Gomb type="submit" toltes={mentes} className="sm:w-fit">Mentés</Gomb>
        </form>
      </section>

      <section className="px-5 py-5">
        <h2 className="mb-2 text-lg font-medium tracking-[-0.015em]">Kétlépcsős azonosítás</h2>
        <HibaUzenet hiba={tenyezok.error} />
        {tenyezok.isPending && <Betolto />}
        {tenyezok.data && (tenyezok.data.length > 0
          ? <ul className="grid gap-1 text-sm">
              <li className="inline-flex items-center gap-1.5 text-siker"><span aria-hidden className="size-[7px] rounded-full bg-current" />Bekapcsolva</li>
              {tenyezok.data.map((t) => (
                <li key={t.id} className="text-szoveg-halk">
                  {t.friendly_name || 'Hitelesítő alkalmazás'}, beállítva: {new Date(t.created_at).toLocaleDateString('hu-HU')}
                </li>
              ))}
            </ul>
          : <p className="text-sm text-szoveg-halk">Nincs beállított tényező.</p>)}
      </section>

      <section className="px-5 py-5">
        <h2 className="mb-2 text-lg font-medium tracking-[-0.015em]">Jogaid</h2>
        <HibaUzenet hiba={tagsagok.error} />
        <ul className="grid gap-1 text-sm">
          {jogok.super && <li>Super admin (minden iroda)</li>}
          {tagsagok.data?.map((t) => <li key={t.office_id}>{irodaNev(t.office_id)}: {SZEREP_CIMKE[t.szerep]}</li>)}
          {tagsagok.data?.length === 0 && !jogok.super && <li className="text-szoveg-halk">Nincs aktív irodai tagságod.</li>}
        </ul>
      </section>
      </Fokonyv>

      <HibaUzenet hiba={kilepesHiba} />
      <Gomb valtozat="masodlagos" className="sm:w-fit" onClick={() => { setKilepesHiba(null); kijelentkezes().catch(setKilepesHiba); }}>Kijelentkezés</Gomb>
    </div>
  );
}
